import type {
  AppendMessage,
  ExternalThreadQueueAdapter,
} from '@assistant-ui/core';
import {v4 as uuidv4} from 'uuid';
import {useShallow} from 'zustand/react/shallow';

import {getVisiblePromptParts} from '../messageConversions';
import type {PromptContextParams} from '../pageContext';
import {
  builtPromptFromStoreContent,
  builtPromptFromText,
  extractText,
  readBuiltPrompt,
  withPromptContext,
} from '../promptAssembly';
import {
  readAgentConfigOverride,
  readPermissions,
  shouldBypassQueue,
} from '../runConfig';
import {
  type ContentPart,
  hasMeaningfulContent,
  type Thread,
} from '../store/thread';
import {resolveHITLSendBlock} from '../tools/catalog';
import {isAgentChatReadonly} from '../useAgentChatReadonly';
import type {CancelThreadResult} from '../useAgentChatRuntime';
import {type QueuedMessage, queueKey, useQueueStore} from './store';
import {useTrackWBAgentQueue} from './tracking';

/**
 * useQueueAdapter builds the AUI `ExternalThreadQueueAdapter` for
 * `activeThread`, reading and writing that thread's queue. It falls back to
 * `onNew` when a message shouldn't queue (an idle thread with an empty queue,
 * an explicit bypass, or no thread yet).
 */
export function useQueueAdapter(
  activeThread: Thread | undefined,
  params: PromptContextParams,
  onNew: (message: AppendMessage) => Promise<void> | void,
  requestCancelThread: (threadId: string) => Promise<CancelThreadResult>
): ExternalThreadQueueAdapter {
  // Key by the thread's own id (optimistic before promotion, server-assigned
  // after). No active thread means the first send hasn't happened.
  const key = activeThread ? queueKey(activeThread.id) : undefined;
  const activeQueue =
    useQueueStore(useShallow(s => (key != null ? s.queues[key] : undefined))) ??
    [];
  const isRunning = activeThread?.status === 'running';
  const isSendBlockedByHITLTool = resolveHITLSendBlock(activeThread);
  const track = useTrackWBAgentQueue(params, activeThread?.id);

  return {
    items: activeQueue
      // A dispatched item is already in the transcript.
      .filter(m => !m.draining)
      .map(m => ({
        id: m.id,
        prompt: getQueueItemPrompt(m.content),
      })),
    enqueue: (message, _options) => {
      if (isAgentChatReadonly()) {
        return;
      }
      if (shouldBypassQueue(message)) {
        void onNew(message);
        return;
      }

      // NOTE: The composer refuses a blocked send before appending, but a
      // programmatic one reaches here, where a non-empty queue would route it
      // past `onNew`'s guard and let `requestResume` dispatch the head.
      if (isSendBlockedByHITLTool) {
        return;
      }

      // No thread yet, so the first send creates one; nothing to queue against.
      if (key == null) {
        void onNew(message);
        return;
      }

      const queueState = useQueueStore.getState();
      const queue = queueState.queues[key] ?? [];
      if (!isRunning && queue.length === 0) {
        void onNew(message);
        return;
      }

      const queuedMessage = buildQueuedMessage(message, params);
      if (!queuedMessage) {
        return;
      }

      const depthBefore = queue.length;
      queueState.enqueue(key, queuedMessage);
      if (!isRunning) {
        // A live send onto an idle, paused queue is an explicit user resume.
        queueState.requestResume(key);
      }

      // NOTE: `enqueue` drops a message stamped at or before the queue's last
      // clear, so only record the send once the depth actually grew.
      const depth = useQueueStore.getState().queues[key]?.length ?? 0;
      if (depth > depthBefore) {
        track.trackQueued(depth);
      }
    },
    steer: async id => {
      if (isAgentChatReadonly() || key == null || activeThread == null) {
        return;
      }
      const {depth, position} = readQueueRow(key, id);
      track.trackSteered(depth, position);
      useQueueStore.getState().requestSteer(key, id);

      // A `not-running` cancel still force-drains the item on the next terminal
      // via the marker. A rejected cancel leaves the turn running, so drop the
      // marker to re-enable the stop and run-now controls.
      const result = await requestCancelThread(activeThread.id);
      if (result === 'failed') {
        useQueueStore.getState().consumeSteer(key);
      }
    },
    remove: id => {
      if (key != null) {
        const {depth, position} = readQueueRow(key, id);
        track.trackRemoved(depth, position);
        useQueueStore.getState().remove(key, id);
      }
    },
    clear: reason => {
      // AUI calls clear for several reasons, but only an edit should drop
      // queued follow-ups; cancel-run (stop) and reload keep them.
      if (reason === 'edit' && key != null) {
        useQueueStore.getState().clear(key);
      }
    },
  };
}

/**
 * buildQueuedMessage converts an AUI `AppendMessage` into a `QueuedMessage`,
 * baking in prompt context now (rather than at drain time) so a queued item's
 * content is already the same complete, ready-to-send form `onNew` would have
 * produced. Returns null for a message with no meaningful content, mirroring
 * `onNew`'s own no-op.
 */
function buildQueuedMessage(
  message: AppendMessage,
  params: PromptContextParams
): QueuedMessage | null {
  const userPrompt =
    readBuiltPrompt(message) ?? builtPromptFromText(extractText(message));
  if (!hasMeaningfulContent(userPrompt.storeParts)) {
    return null;
  }

  return {
    id: uuidv4(),
    content: withPromptContext(userPrompt, params).storeParts,
    enqueuedAt: Date.now(),
    runConfig: {
      custom: {
        agentConfigOverride: readAgentConfigOverride(message) ?? null,
        permissions: readPermissions(message),
      },
    },
  };
}

/**
 * getQueueItemPrompt derives a queue item's preview, dropping the ambient
 * context {@link withPromptContext} prepends. A run-only prompt has no text for
 * that slice to keep, so it falls back to rendering its run mentions.
 */
function getQueueItemPrompt(content: ContentPart[]): string {
  const preview = builtPromptFromStoreContent(
    getVisiblePromptParts(content)
  ).plainText;
  if (preview !== '') {
    return preview;
  }

  const runParts = content.filter(part => part.type === 'wandb-run');
  return builtPromptFromStoreContent(runParts).plainText;
}

/**
 * readQueueRow reports key's queue depth and the row's 0-based position for a
 * tracked row action, so callers read them before mutating the queue. A row
 * that has already left the queue has no position to report.
 */
function readQueueRow(
  key: string,
  id: string
): {depth: number; position?: number} {
  const queue = useQueueStore.getState().queues[key] ?? [];
  const index = queue.findIndex(m => m.id === id);
  return {depth: queue.length, position: index >= 0 ? index : undefined};
}
